import React, { useEffect, useState } from 'react';
import { RadioButton, StatusMessage } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { IconS24Close24 } from '@is24/cosma-ui-icons';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';
import './VerificationLandingPage.css';
import { VerificationMethod } from '../../@types/VerificationMethod';
import VeriffVerification from '../../components/veriffVerification/VeriffVerification';
import FinApiVerification from '../../components/finApiVerification/FinApiVerification';
import { useTvsState } from '../../utils/useTvsState';
import { trackPageView } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useDarkMode } from '../../hooks/useDarkMode';

interface VerificationLandingPageProps {
  verificationErrorMessage?: string;
  defaultMethod?: VerificationMethod;
}

const VerificationLandingPage: React.FC<VerificationLandingPageProps> = ({
  verificationErrorMessage,
  defaultMethod,
}) => {
  const { t } = useTranslation();
  const { clientId, verificationMethods } = useClientConfig();
  const isVerificationMethodProvided = verificationMethods && verificationMethods.length > 0;
  const showVeriff = !isVerificationMethodProvided || verificationMethods.includes(VerificationMethod.Veriff);
  const showFinapi = !isVerificationMethodProvided || verificationMethods.includes(VerificationMethod.Finapi);

  const getInitialMethod = () => {
    if (defaultMethod === VerificationMethod.Finapi && showFinapi) {
      return VerificationMethod.Finapi;
    }
    return showVeriff ? VerificationMethod.Veriff : VerificationMethod.Finapi;
  };

  const [method, setMethod] = useState<VerificationMethod>(getInitialMethod());
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [, setTvsState] = useTvsState();
  const [errorMessage, setErrorMessage] = useState('');
  const isDarkModeOn = useDarkMode();

  const handleErrorsFromVerificationMethod = (errorMessage: string) => {
    setErrorMessage(t(errorMessage as any));
  };

  useEffect(() => {
    if (!verificationErrorMessage) {
      trackPageView('is24.de.sicherheit.verifizierung.modal', clientId);
    } else {
      setErrorMessage(t(verificationErrorMessage as any));
    }
  }, []);

  return (
    <>
      <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
        <div className='modal-container' style={{ width: 601 }}>
          <div className='modal-title-wrapper'>
            <h4 className='modal-headline'>{t('VerificationLandingPage.heading')}</h4>
            <IconS24Close24
              onClick={() => setTvsState('')}
              data-testid='modal-close-icon'
              className='tvs-close-button'
            />
          </div>
          <div className='modal-content-wrapper display-flex'>
            <div className='modal-content palm-display-flex'>
              {errorMessage && (
                <StatusMessage
                  data-testid='error-message'
                  className='margin-bottom-xxl tvs-status-message'
                  headline={t('VerificationLandingPage.errorHeading')}
                  message={t(errorMessage as any)}
                  statusType='error'
                />
              )}
              <div className='grid grid-flex margin-bottom-m'>
                <IdentificationInfoContent isDarkModeOn={isDarkModeOn} />
              </div>
              <div className='grid-item font-heading-xsmall-bold margin-top-xxl palm-margin-bottom-none'>
                {t('VerificationLandingPage.availableOptions')}
              </div>
              <div className='grid grid-flex margin-top-xxl gutter-horizontal-m margin-top-m palm-margin-bottom-m palm-margin-top-m'>
                {showVeriff && (
                  <div className='grid-item one-whole font-h5'>
                    <RadioButton
                      label='Ausweisdokument'
                      id={VerificationMethod.Veriff}
                      name='verification-method'
                      checked={method === VerificationMethod.Veriff}
                      onChange={(e) => setMethod(e.target.id as VerificationMethod)}
                    />
                  </div>
                )}
                {showFinapi && (
                  <div className='grid-item one-whole grid-justify-center'>
                    <RadioButton
                      label='Online-Banking'
                      id={VerificationMethod.Finapi}
                      name='verification-method'
                      checked={method === VerificationMethod.Finapi}
                      onChange={(e) => setMethod(e.target.id as VerificationMethod)}
                    />
                  </div>
                )}
              </div>
              {modalContent}
            </div>
          </div>
          {method === VerificationMethod.Veriff ? (
            <VeriffVerification
              onContentChange={setModalContent}
              onError={(errorMessage) => handleErrorsFromVerificationMethod(errorMessage)}
              isDarkModeOn={isDarkModeOn}
            />
          ) : (
            <FinApiVerification
              onContentChange={setModalContent}
              onError={(errorMessage) => handleErrorsFromVerificationMethod(errorMessage)}
              isDarkModeOn={isDarkModeOn}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default VerificationLandingPage;
